/**
 * /discount-codes - the codes OCP issued, the day's cap, issue and revoke
 * (OCP-991). Same routes and audit row as the MCP tools ocp_list_discount_codes,
 * ocp_issue_discount_code and ocp_revoke_discount_code (OCP-599).
 *
 * Terms and cap are settings (billing.discount_*), they are not edited here:
 * this page shows them and links to Settings, the one place that changes them.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { fmtDate, fmtDateTime } from '../lib/format-time';

type Plan = 'team' | 'business';
type Status = 'reserved' | 'active' | 'redeemed' | 'revoked' | 'expired';

interface DiscountCodeRow {
  id: string;
  code: string;
  plan: Plan;
  percent: number;
  duration: 'once' | 'forever' | 'repeating';
  duration_in_months: number | null;
  expires_at: string;
  status: Status;
  redeem_url: string;
  source: 'www' | 'mcp' | 'admin-ui';
  issued_by: string;
  issue_reason: string | null;
  redeemed_order_id: string | null;
  redeemed_at: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  created_at: string;
}

interface ListResponse {
  items: DiscountCodeRow[];
  total: number;
  day: string;
  issued_today: number;
  daily_cap: number;
}

const STATUSES: Status[] = ['active', 'redeemed', 'revoked', 'expired', 'reserved'];
const MIN_REASON = 10;

const STATUS_CLASSES: Record<Status, string> = {
  active: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  redeemed: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  revoked: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  expired: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  reserved: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

function durationLabel(row: Pick<DiscountCodeRow, 'duration' | 'duration_in_months'>): string {
  if (row.duration === 'once') return 'first invoice';
  if (row.duration === 'forever') return 'every invoice';
  return `${row.duration_in_months} months`;
}

function errorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  const code = (err.body as { error?: string } | null)?.error;
  if (code === 'daily_cap_reached') return 'The daily cap is reached. Tick "Pass the daily cap" to issue this one code past it.';
  if (code === 'discount_unavailable') return 'The payment provider is unavailable. Nothing was changed; try again.';
  if (code === 'discount_code_redeemed') {
    return 'The payment provider has already redeemed this code (the customer confirmed the order), so it cannot be revoked. The payment event marks it redeemed here.';
  }
  return `${fallback} (HTTP ${err.status}${code ? `, ${code}` : ''}).`;
}

export function AdminDiscountCodesPage() {
  const [status, setStatus] = useState<Status | 'all'>('all');
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [revoking, setRevoking] = useState<DiscountCodeRow | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const q = status === 'all' ? '' : `?status=${status}`;
      setData(await api<ListResponse>(`/v1/admin/discount-codes${q}`));
    } catch (err) {
      setError(errorMessage(err, 'Could not load the discount codes'));
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Discount codes</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Single-use codes for both intervals of a plan. Issued for website visitors through the signed endpoint,
            or here. Percent, duration, validity and the daily cap are in{' '}
            <Link to="/settings" className="underline">Settings</Link> (billing.discount_*); a code keeps the terms it
            was issued with.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIssuing(true)}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          Issue a code
        </button>
      </div>

      {data && (
        <p className="mt-4 text-sm text-gray-700 dark:text-gray-300" data-testid="discount-cap">
          Today ({data.day}, Europe/Berlin): <span className="font-semibold">{data.issued_today}</span> of{' '}
          <span className="font-semibold">{data.daily_cap}</span> issued.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm" role="group" aria-label="Status">
        {(['all', ...STATUSES] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            aria-pressed={status === s}
            className={`rounded px-3 py-1 ring-1 ring-inset ${status === s ? 'bg-brand-500 text-white ring-brand-500' : 'text-gray-700 ring-gray-300 dark:text-gray-300 dark:ring-gray-700'}`}
          >
            {s}
          </button>
        ))}
      </div>

      {error && (
        <div role="alert" className="mt-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="mt-6 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
          No discount code in this view.
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Plan</th>
                <th className="px-3 py-2">Discount</th>
                <th className="px-3 py-2">State</th>
                <th className="px-3 py-2">Valid until</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Issued</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.items.map((c) => (
                <tr key={c.id} data-testid="discount-code-row">
                  <td className="px-3 py-2 font-mono text-xs">{c.code}</td>
                  <td className="px-3 py-2">{c.plan}</td>
                  <td className="px-3 py-2">{c.percent} %, {durationLabel(c)}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[c.status]}`}>{c.status}</span>
                    {c.revoke_reason && <span className="ml-1 text-xs text-gray-500">{c.revoke_reason}</span>}
                    {c.redeemed_at && <span className="ml-1 text-xs text-gray-500">{fmtDateTime(c.redeemed_at)}</span>}
                  </td>
                  <td className="px-3 py-2">{fmtDate(c.expires_at)}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{c.source}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{fmtDateTime(c.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    {(c.status === 'active' || c.status === 'expired') && (
                      <button
                        type="button"
                        onClick={() => setRevoking(c)}
                        className="text-xs text-red-700 hover:underline dark:text-red-300"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {issuing && (
        <IssueDialog
          onClose={() => setIssuing(false)}
          onDone={() => {
            setIssuing(false);
            void load();
          }}
        />
      )}
      {revoking && (
        <RevokeDialog
          row={revoking}
          onClose={() => setRevoking(null)}
          onDone={() => {
            setRevoking(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl">
        <h2 className="text-base font-semibold">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function IssueDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [plan, setPlan] = useState<Plan>('team');
  const [reason, setReason] = useState('');
  const [overrideCap, setOverrideCap] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<DiscountCodeRow | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      setIssued(
        await api<DiscountCodeRow>('/v1/admin/discount-codes', {
          method: 'POST',
          body: { plan, reason: reason.trim(), ...(overrideCap ? { override_cap: true } : {}) },
        }),
      );
    } catch (err) {
      setError(errorMessage(err, 'Could not issue the code'));
    } finally {
      setBusy(false);
    }
  }

  if (issued) {
    return (
      <Dialog title="Code issued" onClose={onDone}>
        <p className="mt-3 font-mono text-sm" data-testid="discount-issued-code">{issued.code}</p>
        <p className="mt-1 break-all text-xs text-gray-600 dark:text-gray-400">{issued.redeem_url}</p>
        <div className="mt-5 flex justify-end">
          <button type="button" onClick={onDone} className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600">Done</button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title="Issue a discount code" onClose={onClose}>
      <label className="mt-3 block text-sm">
        Plan
        <select value={plan} onChange={(e) => setPlan(e.target.value as Plan)} className="mt-1 block w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm">
          <option value="team">Team</option>
          <option value="business">Business</option>
        </select>
      </label>
      <label className="mt-3 block text-sm">
        Reason (recorded on the audit row)
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} className="mt-1 block w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm" />
      </label>
      <label className="mt-3 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={overrideCap} onChange={(e) => setOverrideCap(e.target.checked)} />
        Pass the daily cap for this code
      </label>
      {error && <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm">Cancel</button>
        <button type="button" disabled={busy || reason.trim().length < MIN_REASON} onClick={() => void submit()} className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
          Issue
        </button>
      </div>
    </Dialog>
  );
}

function RevokeDialog({ row, onClose, onDone }: { row: DiscountCodeRow; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/admin/discount-codes/${row.id}/revoke`, { method: 'POST', body: { reason: reason.trim() } });
      onDone();
    } catch (err) {
      setError(errorMessage(err, 'Could not revoke the code'));
      setBusy(false);
    }
  }

  return (
    <Dialog title={`Revoke ${row.code}`} onClose={onClose}>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">The code stops working here and at the payment provider.</p>
      <label className="mt-3 block text-sm">
        Reason (recorded on the audit row)
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} className="mt-1 block w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm" />
      </label>
      {error && <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm">Cancel</button>
        <button type="button" disabled={busy || reason.trim().length < MIN_REASON} onClick={() => void submit()} className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
          Revoke
        </button>
      </div>
    </Dialog>
  );
}
